import { useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Reveal, RevealLines, easeSmooth } from "@/lib/motion";
import { ArrowUpRight, MessageCircle, Phone, Mail, MapPin, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP =
  "https://wa.me/15197029417?text=" +
  encodeURIComponent("Hi Yach Construction — I saw your ad and want a free quote.");

const projectTypes = ["Residential", "Commercial", "Heritage / Restoration", "Not sure yet"];

const perks = [
  "Free on-site consultation",
  "Fixed-price quote in 48 hours",
  "6 years, 80+ projects, on-time delivery",
  "Fully licensed & insured in Ontario",
];

export default function Quote() {
  const { search } = useLocation();
  const utm = new URLSearchParams(search).get("utm_source") || "quote-page";
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    project_type: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please fill your name, email and phone.");
      return;
    }
    try {
      setSending(true);
      await axios.post(`${API}/contact`, {
        ...form,
        message: form.message || "Requested a free quote.",
        source: `quote:${utm}`,
      });
      setSent(true);
      toast.success("Quote request received.");
    } catch (err) {
      toast.error("Something went wrong.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="quote-page" className="pt-28 md:pt-32 min-h-screen">
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-12">
        <span className="eyebrow">Free Quote · No Obligation</span>
        <div className="mt-6">
          <RevealLines
            className="font-display text-[12vw] md:text-[8vw] leading-[0.9] tracking-tighter"
            lines={[
              <>Get a fixed-price</>,
              <><em className="hero-italic">quote in 48 hours.</em></>,
            ]}
            stagger={0.14}
          />
        </div>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-24 grid grid-cols-1 md:grid-cols-12 gap-10">
        {/* Trust column */}
        <aside className="md:col-span-5 md:pr-8 border-t border-[#D9D4C7] pt-8">
          <Reveal>
            <p className="font-display text-2xl md:text-3xl leading-[1.2] tracking-tight text-[#1A1A1A] max-w-md">
              Tell us about your build. We'll walk your site, prepare a fixed
              scope, and come back with a real number — not a range.
            </p>
            <ul className="mt-8 space-y-4">
              {perks.map((p) => (
                <li key={p} className="flex items-start gap-3 font-grotesk text-[15px]">
                  <Check className="w-4 h-4 mt-1 text-[#8C7A6B] flex-shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 grid grid-cols-1 gap-3 max-w-sm">
              <a href={WHATSAPP} target="_blank" rel="noreferrer noopener" className="btn-sharp" style={{ background: "#25D366" }} data-testid="quote-whatsapp">
                <MessageCircle className="w-4 h-4" />
                <span>WhatsApp us now</span>
              </a>
              <a href="tel:+15197029417" className="btn-ghost" data-testid="quote-call">
                <Phone className="w-4 h-4" />
                <span>Call +1 519-702-9417</span>
              </a>
            </div>

            <div className="mt-10 pt-6 border-t border-[#D9D4C7] space-y-2 font-grotesk text-sm">
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-[#8C7A6B]" /> London, Ontario · Canada
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#8C7A6B]" /> Yachconstruction1@gmail.com
              </div>
            </div>
          </Reveal>
        </aside>

        {/* Form */}
        <div className="md:col-span-7 md:pl-8 md:border-l border-t md:border-t-0 border-[#D9D4C7] pt-8 md:pt-0">
          {sent ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: easeSmooth }}
              className="min-h-[420px] flex flex-col justify-center"
              data-testid="quote-sent"
            >
              <span className="eyebrow">Received</span>
              <h3 className="mt-6 font-display text-5xl md:text-6xl leading-[0.95] tracking-tight">
                Thanks — we'll call you within 24 hours.
              </h3>
              <p className="mt-6 max-w-lg font-grotesk text-[15px] text-[#4A4A4A] leading-[1.7]">
                A principal from the studio will personally review your enquiry and
                schedule a site visit. If it can't wait, WhatsApp us at +1 519-702-9417.
              </p>
              <div className="mt-8">
                <a href={WHATSAPP} target="_blank" rel="noreferrer noopener" className="btn-sharp" style={{ background: "#25D366" }}>
                  <MessageCircle className="w-4 h-4" />
                  <span>Message us on WhatsApp</span>
                </a>
              </div>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="space-y-2" data-testid="quote-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                <FField label="Full name" required v={form.name} onChange={(v) => setField("name", v)} testid="quote-name" />
                <FField label="Phone" required type="tel" v={form.phone} onChange={(v) => setField("phone", v)} testid="quote-phone" />
                <FField label="Email" required type="email" v={form.email} onChange={(v) => setField("email", v)} testid="quote-email" className="md:col-span-2" />
                <div className={`field ${form.project_type ? "filled" : ""} md:col-span-2`}>
                  <select
                    value={form.project_type}
                    onChange={(e) => setField("project_type", e.target.value)}
                    data-testid="quote-project-type"
                  >
                    <option value="" />
                    {projectTypes.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                  <label>Project type</label>
                </div>
                <div className={`field ${form.message ? "filled" : ""} md:col-span-2`}>
                  <textarea
                    rows={4}
                    value={form.message}
                    onChange={(e) => setField("message", e.target.value)}
                    data-testid="quote-message"
                  />
                  <label>Anything we should know? (optional)</label>
                </div>
              </div>
              <div className="pt-10 flex items-center justify-between gap-4 flex-wrap">
                <p className="font-grotesk text-[11px] uppercase tracking-[0.22em] text-[#4A4A4A] max-w-xs">
                  No obligation. We reply within one business day.
                </p>
                <button type="submit" className="btn-sharp" disabled={sending} data-testid="quote-submit">
                  <span>{sending ? "Sending…" : "Get my free quote"}</span>
                  <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}

function FField({ label, v, onChange, type = "text", required, testid, className = "" }) {
  return (
    <div className={`field ${v ? "filled" : ""} ${className}`}>
      <input
        type={type}
        value={v}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        required={required}
      />
      <label>{label}{required ? " *" : ""}</label>
    </div>
  );
}
